// Уведомления: чистые расчёты (мини-график цены, подписи подписки, описание в окне «Следить за ценой»)
import type { AlertView } from '@sayohat/shared';
import { formatDateOrMonth } from '../../shared/lib/format';
import type { WatchRequest } from '../../shared/lib/types';

export interface Spark {
  d: string;
  /** цена снизилась с первой точки */
  down: boolean;
  w: number;
  h: number;
}

/** Путь мини-графика истории цены 120×36; меньше двух точек — графика нет */
export function sparkPath(history: { price: number | null }[] | null | undefined, w = 120, h = 36): Spark | null {
  const pts = (history || []).filter((x): x is { price: number } => x.price !== null);
  if (pts.length < 2) return null;
  const min = Math.min(...pts.map((p) => p.price)), max = Math.max(...pts.map((p) => p.price));
  const y = (p: number) => (max === min ? h / 2 : h - 4 - ((p - min) / (max - min)) * (h - 8));
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${((i / (pts.length - 1)) * (w - 4) + 2).toFixed(1)},${y(p.price).toFixed(1)}`).join(' ');
  return { d, down: pts[pts.length - 1].price < pts[0].price, w, h };
}

/** Заголовок подписки: «Отель, город» или «Откуда — Куда» */
export const alertTitle = (a: AlertView): string => (a.kind === 'hotel' ? `${a.originName}, ${a.destinationName}` : `${a.originName} — ${a.destinationName}`);

/** Даты и условия подписки */
export function alertDates(a: AlertView): string {
  if (a.kind === 'hotel') return `${formatDateOrMonth(a.departure)} — ${formatDateOrMonth(a.ret)}, цена за ночь`;
  if (a.kind === 'train') return `${formatDateOrMonth(a.departure)}${a.trainNumber ? `, поезд ${a.trainNumber}` : ', любой поезд'}`;
  return `${formatDateOrMonth(a.departure)}${a.ret ? ` — ${formatDateOrMonth(a.ret)}` : ', в одну сторону'}${a.direct ? ' · только прямые' : ''}`;
}

/** Нет цены: чего ждём */
export const noPriceText = (a: AlertView): string => (a.kind === 'train' ? 'мест пока нет' : a.kind === 'hotel' ? 'номеров пока нет' : 'билетов пока нет');

/** Описание в окне «Следить за ценой» */
export function watchText(q: WatchRequest): string {
  if (q.kind === 'hotel') return `Отель ${q.originName}, ${formatDateOrMonth(q.departure)} — ${formatDateOrMonth(q.ret || '')}. Сообщим, когда цена за ночь снизится или появятся номера.`;
  if (q.kind === 'train') return `Поезд ${q.originName} — ${q.destinationName}, ${formatDateOrMonth(q.departure)}${q.trainNumber ? `, ${q.trainBrand || ''} ${q.trainNumber}` : ', любой поезд'}. Сообщим, когда появятся места.`;
  return `${q.originName || q.origin} — ${q.destinationName || q.destination}, `
    + `${formatDateOrMonth(q.departure)}${q.ret ? ` — ${formatDateOrMonth(q.ret)}` : ', в одну сторону'}${q.direct ? ', только прямые' : ''}`;
}

/** Окно открывается для подписки: у отеля destination пустой, но есть ключ отеля */
export const canWatch = (q: WatchRequest | null | undefined): q is WatchRequest => Boolean(q && (q.destination || (q.kind === 'hotel' && q.origin)));

/** Публичный VAPID-ключ (base64url) → байты для pushManager.subscribe */
export function b64ToBytes(s: string): Uint8Array<ArrayBuffer> {
  const b = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4));
  return Uint8Array.from(b, (c) => c.charCodeAt(0));
}
